import { query } from 'bitecs';
import { Config } from '@src/core/Config';
import { difficulty, type DifficultyProfile } from '@src/core/Difficulty';
import { PoolKind } from '@src/core/Pools';
import { NO_ENTITY, spawnBat, spawnBlight, spawnBrute, spawnCinder, spawnGolem, spawnHexer, spawnImp, spawnSkull, spawnSlime, spawnWraith } from '@src/ecs/archetypes';
import { Anim, Dying, EnemyKind, Health, Position, Spawner, Sprite, Velocity } from '@src/ecs/components';
import type { GameSystem } from '@src/ecs/System';
import { emitParticles, ParticleKind, RoomPhase, type GameContext } from '@src/ecs/world';
import { floorMod, type FloorModifierDef } from '@src/game/FloorModifiers';
import { heavyKind, rollSmallFry, smallFryWeights } from '@src/game/SpawnRoster';

/**
 * How many small fry one cough should spit. Capped per difficulty so late rooms
 * do not quietly return to a blob. Swarm floors add one and lift the cap by the same.
 *
 * DEV: liveCap (Difficulty) is the density ceiling. Do not raise burstMax /
 * burstBonus or this formula to "fill" a room — portals refill as kills free slots.
 */
function burstSize(roomTier: number, tier: DifficultyProfile, rule: FloorModifierDef): number {
  const raw = 2 + Math.min(2, Math.floor(roomTier / 2)) + tier.burstBonus + rule.burstBonus;
  return Math.max(1, Math.min(tier.burstMax + rule.burstBonus, raw));
}

/**
 * Arcane Portals pump out imps (and the occasional golem) on a timer. The fewer
 * portals remain, the faster the survivors pump, so the last one is always a fight.
 * Small-fry count is also clamped to Difficulty.liveCap so the pack stays readable.
 */
export class SpawnerSystem implements GameSystem {
  private ctx!: GameContext;
  private moteAccum = 0;

  init(ctx: GameContext): void {
    this.ctx = ctx;
  }

  destroy(): void {}

  update(dt: number): void {
    const ctx = this.ctx;
    if (ctx.room.phase !== RoomPhase.Fight) return;

    const spawners = query(ctx.world, [Spawner, Position, Health]);
    let alive = 0;
    for (let i = 0; i < spawners.length; i++) if (!Dying.flag[spawners[i]]) alive++;
    const pressure = alive > 0 ? 1 + (ctx.room.spawnersAlive - alive + 1) * 0.15 : 1;
    const c = Config.enemies.spawner;
    const tier = difficulty();
    const rule = floorMod(ctx.floor.modifier);
    const golemEvery = rule.golemEvery > 0 ? rule.golemEvery : c.golemEvery;
    const liveCap = tier.liveCap + rule.liveCapBonus;
    this.moteAccum += dt;
    const emitMotes = this.moteAccum >= 0.08;
    if (emitMotes) this.moteAccum = 0;

    // Shared across portals this tick so three mouths cannot overshoot the cap together.
    let liveFry = ctx.pools.count(PoolKind.Imp);

    for (let i = 0; i < spawners.length; i++) {
      const eid = spawners[i];
      if (Dying.flag[eid]) continue;
      Spawner.timer[eid] -= dt * pressure * ctx.difficulty * tier.spawnRate;
      const charge = 1 - Math.max(0, Spawner.timer[eid]) / Spawner.interval[eid];
      const maxHp = Health.max[eid] || 1;
      const wound = 1 - Math.max(0, Math.min(1, Health.hp[eid] / maxHp));
      const crit = wound > 0.65;
      const wobble = crit ? Math.sin(ctx.time * 17 + eid) * 0.07 * wound : 0;
      // Healthy gates sit large and breathe with the cough. Wounded ones shrink
      // and go unsteady; critical mouths twitch instead of pulsing cleanly.
      Sprite.scale[eid] = 3.05 + charge * 0.42 - wound * 0.32 + wobble;
      Sprite.alpha[eid] = 0.9 + charge * 0.1 - wound * 0.12;
      if (crit) Anim.speed[eid] = 1.55 + charge * 1.8 + Math.sin(ctx.time * 11 + eid) * 0.7;
      else if (wound > 0.32) Anim.speed[eid] = 0.55 + charge * 0.95 + wound * 0.35;
      else Anim.speed[eid] = 0.85 + charge * 1.45;
      const spin = crit ? 2.4 + charge * 1.6 : 0.35 + charge * 1.35 + wound * 0.45;
      Sprite.rot[eid] += dt * spin;
      if (emitMotes) {
        emitParticles(ctx.fx, ParticleKind.PortalMotes, Position.x[eid], Position.y[eid], 0, 0, 0.22 + charge * 0.55 + wound * 0.35);
        if (wound > 0.55) emitParticles(ctx.fx, ParticleKind.PortalMotes, Position.x[eid], Position.y[eid], 0, 0, 0.4 + wound * 0.4, 0x6a4450);
      }
      if (Spawner.timer[eid] > 0) continue;

      const wantsGolem = (Spawner.spawned[eid] + 1) % golemEvery === 0 && ctx.room.tier >= 1;
      if (!wantsGolem && liveFry >= liveCap) {
        // Hold the charge and retry shortly; kills will open a slot.
        Spawner.timer[eid] = 0.28;
        continue;
      }

      Spawner.timer[eid] = Spawner.interval[eid];
      Spawner.spawned[eid]++;
      Sprite.squash[eid] = 0.8;
      emitParticles(ctx.fx, ParticleKind.PortalBurst, Position.x[eid], Position.y[eid], 0, 0, 0.6);
      const ang = ctx.rng.next() * Math.PI * 2;
      const ring = ctx.rng.range(c.burstRingMin, c.burstRingMax);
      const px = Position.x[eid];
      const py = Position.y[eid];

      if (wantsGolem) {
        const gx = px + Math.cos(ang) * ring;
        const gy = py + Math.sin(ang) * ring;
        // Heavy beat: golem, or every other one a brute once they have debuted.
        const heavyIndex = Math.floor(Spawner.spawned[eid] / golemEvery);
        const spawnHeavy = heavyKind(ctx.room.tier, heavyIndex, ctx.floor.modifier) === EnemyKind.Brute ? spawnBrute : spawnGolem;
        const golem = spawnHeavy(ctx, gx, gy);
        if (golem !== NO_ENTITY) {
          Velocity.x[golem] = Math.cos(ang) * c.burstPop * 0.35;
          Velocity.y[golem] = Math.sin(ang) * c.burstPop * 0.35;
        }
        continue;
      }
      // Burst spawn: one kind per cough so a portal reads as "a pack of bats".
      // Fan onto a wide ring with a short outward pop so the pack does not start stacked.
      const spawn = this.spawnFn(this.pickSmallFry());
      const burst = Math.min(burstSize(ctx.room.tier, tier, rule), Math.max(0, liveCap - liveFry));
      if (burst <= 0) continue;
      for (let b = 0; b < burst; b++) {
        const a2 = ang + (b / burst) * Math.PI * 2 + ctx.rng.range(-0.2, 0.2);
        const d2 = ring + ctx.rng.range(-0.15, 0.25);
        const ox = Math.cos(a2);
        const oy = Math.sin(a2);
        const spawned = spawn(ctx, px + ox * d2, py + oy * d2);
        if (spawned === NO_ENTITY) continue;
        liveFry++;
        Velocity.x[spawned] = ox * c.burstPop;
        Velocity.y[spawned] = oy * c.burstPop;
      }
    }
  }

  /** Room featured kind is the star; leftover bursts stay mostly imps plus older guests. */
  private pickSmallFry(): EnemyKind {
    const room = this.ctx.room;
    return rollSmallFry(smallFryWeights(room.tier, room.door, room.featured, this.ctx.floor.modifier), this.ctx.rng.next());
  }

  private spawnFn(kind: EnemyKind): (ctx: GameContext, x: number, y: number) => number {
    switch (kind) {
      case EnemyKind.Skull:
        return spawnSkull;
      case EnemyKind.Slime:
        return spawnSlime;
      case EnemyKind.Bat:
        return spawnBat;
      case EnemyKind.Hexer:
        return spawnHexer;
      case EnemyKind.Cinder:
        return spawnCinder;
      case EnemyKind.Blight:
        return spawnBlight;
      case EnemyKind.Wraith:
        return spawnWraith;
      default:
        return spawnImp;
    }
  }
}
